import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getServicesFromStorage, getServiceKey } from '../../HomePage/serviceFormatter';
import seedMockDataIfNeeded from '../../CSCOwnerPortal/CSCOwnerServices/seedMockData';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PublicIcon from '@mui/icons-material/Public';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import MoneyOffOutlinedIcon from '@mui/icons-material/MoneyOffOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import SentimentDissatisfiedOutlinedIcon from '@mui/icons-material/SentimentDissatisfiedOutlined';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { getFieldConfig } from '../../../constants/serviceFieldConfig';
import './ServicesExplorer.styles.scss';

// ── Constants ─────────────────────────────────────────────────────────────────
const COMMON_CATEGORIES = new Set([
  'Aadhaar Services', 'PAN Card Services', 'Passport Services', 'Voter ID Services',
  'Driving Licence', 'Birth / Death Certificate', 'Caste / Domicile Certificate',
  'Income Certificate', 'Residence Certificate', 'Ration Card Services',
  'Jan Dhan & Banking Services', 'Digital Payments & UPI',
]);

const SORT_OPTIONS = [
  { value: 'latest',       label: 'Latest First' },
  { value: 'closing-soon', label: 'Closing Soon' },
  { value: 'name',         label: 'Name A → Z' },
  { value: 'fee-low',      label: 'Fee: Low → High' },
];

const STATUS_OPTIONS = [
  { value: 'all',      label: 'All' },
  { value: 'active',   label: 'Active' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'closed',   label: 'Closed' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const getStatus = (startDate, endDate) => {
  const now = new Date();
  const start = startDate ? new Date(startDate) : null;
  const end   = endDate   ? new Date(endDate)   : null;
  if (start && start > now) return 'upcoming';
  if (end   && end   < now) return 'closed';
  return 'active';
};

const isClosingSoon = (endDate) => {
  if (!endDate) return false;
  const diff = (new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= 7;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return dateStr; }
};

const isFreeService = (svc) =>
  !svc.feeGeneral || svc.feeGeneral === '0' || svc.feeGeneral === 0;

const deduplicateServices = (services) => {
  const seen = new Set();
  return services.filter((svc) => {
    const key = getServiceKey(svc);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// ── CompareModal ───────────────────────────────────────────────────────────────
const COMPARE_ROWS = [
  { label: 'Category',      key: (s) => s.serviceCategory || '—' },
  { label: 'State',         key: (s) => s.state || 'All India' },
  { label: 'Status',        key: (s) => getStatus(s.startDate, s.endDate) },
  { label: 'Start Date',    key: (s) => fmtDate(s.startDate) || '—',  configKey: 'dates' },
  { label: 'End Date',      key: (s) => fmtDate(s.endDate) || '—',    configKey: 'dates' },
  { label: 'General Fee',   key: (s) => isFreeService(s) ? 'FREE' : `₹${s.feeGeneral}` },
  { label: 'OBC Fee',       key: (s) => s.feeObc   ? `₹${s.feeObc}`   : '—' },
  { label: 'SC/ST Fee',     key: (s) => s.feeScSt  ? `₹${s.feeScSt}`  : 'Free' },
  { label: 'EWS Fee',       key: (s) => s.feeEws   ? `₹${s.feeEws}`   : '—' },
  { label: 'Min Age',       key: (s) => s.minAge    ? `${s.minAge} yrs` : '—', configKey: 'age' },
  { label: 'Max Age',       key: (s) => s.maxAge    ? `${s.maxAge} yrs` : '—', configKey: 'age' },
  { label: 'Qualification', key: (s) => (s.qualifications || []).filter(Boolean).join(', ') || '—', configKey: 'qualifications' },
  { label: 'Documents',     key: (s) => (s.requiredDocuments || []).filter(Boolean).join(', ') || '—' },
];

const CompareModal = ({ services, onClose, onNavigate }) => {
  if (!services.length) return null;
  // Show a row only if it has no configKey OR at least one compared service has that field enabled
  const visibleRows = COMPARE_ROWS.filter((row) => {
    if (!row.configKey) return true;
    return services.some((svc) => getFieldConfig(svc.serviceCategory)[row.configKey]);
  });
  return (
    <div className="sexp-cmp-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sexp-cmp-modal">
        <div className="sexp-cmp-modal__header">
          <div className="sexp-cmp-modal__title">
            <CompareArrowsIcon style={{ fontSize: '1.2rem' }} />
            Compare Services
          </div>
          <button className="sexp-cmp-modal__close" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="sexp-cmp-modal__body">
          <table className="sexp-cmp-table">
            <thead>
              <tr>
                <th className="sexp-cmp-table__row-label" />
                {services.map((svc) => (
                  <th key={svc.serviceName} className="sexp-cmp-table__svc-head">
                    <div className="sexp-cmp-svc-name">{svc.serviceName}</div>
                    <button
                      className="sexp-cmp-view-btn"
                      onClick={() => onNavigate(svc)}
                    >
                      View Details <ArrowForwardIcon style={{ fontSize: '0.75rem' }} />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.label} className="sexp-cmp-table__row">
                  <td className="sexp-cmp-table__row-label">{row.label}</td>
                  {services.map((svc) => {
                    const val = row.key(svc);
                    const isFree = val === 'FREE';
                    const isClosed = val === 'closed';
                    return (
                      <td
                        key={svc.serviceName}
                        className={`sexp-cmp-table__cell${isFree ? ' sexp-cmp-table__cell--free' : ''}${isClosed ? ' sexp-cmp-table__cell--closed' : ''}`}
                      >
                        {val}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── FilterDropdown ─────────────────────────────────────────────────────────────
const FilterDropdown = ({ label, options, selected, onToggle, onClear, isOpen, onToggleOpen }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onToggleOpen(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onToggleOpen]);

  return (
    <div ref={ref} className={`sexp-dropdown${isOpen ? ' sexp-dropdown--open' : ''}`}>
      <button
        className={`sexp-filter-btn${selected.length ? ' sexp-filter-btn--active' : ''}`}
        onClick={onToggleOpen}
      >
        <span>{label}</span>
        {selected.length > 0 && <span className="sexp-filter-count">{selected.length}</span>}
        <KeyboardArrowDownIcon className={`sexp-filter-arrow${isOpen ? ' sexp-filter-arrow--open' : ''}`} fontSize="small" />
      </button>
      {isOpen && (
        <div className="sexp-dropdown-menu">
          <div className="sexp-dropdown-menu__header">
            <span>{label}</span>
            {selected.length > 0 && (
              <button className="sexp-dropdown-menu__clear" onClick={onClear}>Clear</button>
            )}
          </div>
          <div className="sexp-dropdown-menu__list">
            {options.map((opt) => (
              <button
                key={opt}
                className={`sexp-dropdown-option${selected.includes(opt) ? ' sexp-dropdown-option--active' : ''}`}
                onClick={() => onToggle(opt)}
              >
                <span className={`sexp-check${selected.includes(opt) ? ' sexp-check--on' : ''}`}>
                  {selected.includes(opt) && <CheckIcon style={{ fontSize: '0.75rem' }} />}
                </span>
                <span>{opt}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── SmartToggle ────────────────────────────────────────────────────────────────
const SmartToggle = ({ label, icon, active, onClick }) => (
  <button className={`sexp-smart-toggle${active ? ' sexp-smart-toggle--on' : ''}`} onClick={onClick}>
    {icon}
    <span>{label}</span>
  </button>
);

// ── SortDropdown ──────────────────────────────────────────────────────────────
const SortDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  const current = SORT_OPTIONS.find((o) => o.value === value);
  return (
    <div ref={ref} className={`sexp-dropdown sexp-dropdown--sort${open ? ' sexp-dropdown--open' : ''}`}>
      <button className="sexp-filter-btn sexp-filter-btn--sort" onClick={() => setOpen((p) => !p)}>
        <SwapVertIcon fontSize="small" />
        <span>{current?.label}</span>
        <KeyboardArrowDownIcon className={`sexp-filter-arrow${open ? ' sexp-filter-arrow--open' : ''}`} fontSize="small" />
      </button>
      {open && (
        <div className="sexp-dropdown-menu sexp-dropdown-menu--right">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`sexp-dropdown-option${value === opt.value ? ' sexp-dropdown-option--active' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              <span className={`sexp-check${value === opt.value ? ' sexp-check--on' : ''}`}>
                {value === opt.value && <CheckIcon style={{ fontSize: '0.75rem' }} />}
              </span>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── ServiceGridCard ────────────────────────────────────────────────────────────
const ServiceGridCard = ({ service, onClick, compareSelected, onCompareToggle, compareDisabled }) => {
  const cfg         = getFieldConfig(service.serviceCategory);
  const status      = getStatus(service.startDate, service.endDate);
  const free        = isFreeService(service);
  const closeSoon   = isClosingSoon(service.endDate);
  const isCentral   = service.state === 'All India';

  return (
    <article
      className={`sexp-card sexp-card--${status}${compareSelected ? ' sexp-card--comparing' : ''}`}
      onClick={() => onClick(service)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick(service)}
    >
      {/* Top badges row */}
      <div className="sexp-card__badges">
        <span className={`sexp-status sexp-status--${status}`}>{status}</span>
        {isCentral ? (
          <span className="sexp-tag sexp-tag--central"><PublicIcon style={{ fontSize: '0.7rem' }} />Central</span>
        ) : service.state ? (
          <span className="sexp-tag sexp-tag--state"><LocationOnOutlinedIcon style={{ fontSize: '0.7rem' }} />{service.state}</span>
        ) : null}
        {closeSoon && <span className="sexp-tag sexp-tag--urgent"><LocalFireDepartmentIcon style={{ fontSize: '0.7rem' }} />Closing Soon</span>}
        {free && <span className="sexp-tag sexp-tag--free">FREE</span>}
      </div>

      {/* Category label */}
      <p className="sexp-card__category">{service.serviceCategory}</p>

      {/* Title */}
      <h3 className="sexp-card__title">{service.serviceName}</h3>

      {/* Description */}
      {service.serviceDescription && (
        <p className="sexp-card__desc">{service.serviceDescription}</p>
      )}

      {/* Info rows */}
      <div className="sexp-card__info">
        {cfg.qualifications && service.qualifications?.filter(Boolean).length > 0 && (
          <div className="sexp-card__info-row">
            <SchoolOutlinedIcon className="sexp-card__info-icon" />
            <span>
              {service.qualifications.slice(0, 2).join(' · ')}
              {service.qualifications.length > 2 ? ` +${service.qualifications.length - 2}` : ''}
            </span>
          </div>
        )}
        {cfg.dates && (service.startDate || service.endDate) && (
          <div className="sexp-card__info-row">
            <CalendarTodayOutlinedIcon className="sexp-card__info-icon" />
            <span>{fmtDate(service.startDate) || '–'} → {fmtDate(service.endDate) || '–'}</span>
          </div>
        )}
        {service.feeGeneral !== undefined && service.feeGeneral !== '' && (
          <div className="sexp-card__info-row">
            <CurrencyRupeeIcon className="sexp-card__info-icon" />
            <span>
              {free ? 'No application fee' : `₹${service.feeGeneral} (General)`}
              {service.feeScSt === '0' || !service.feeScSt ? ' · Free for SC/ST' : ''}
            </span>
          </div>
        )}
        {cfg.age && (service.minAge || service.maxAge) && (
          <div className="sexp-card__info-row">
            <VerifiedOutlinedIcon className="sexp-card__info-icon" />
            <span>Age: {service.minAge || '–'} – {service.maxAge || '–'} yrs</span>
          </div>
        )}
      </div>

      {/* CTA footer */}
      <div className="sexp-card__footer">
        <span className="sexp-card__cta">
          View Details
          <ArrowForwardIcon className="sexp-card__cta-icon" fontSize="small" />
        </span>
        <button
          className={`sexp-card__compare-btn${compareSelected ? ' sexp-card__compare-btn--on' : ''}${compareDisabled ? ' sexp-card__compare-btn--disabled' : ''}`}
          onClick={(e) => { e.stopPropagation(); if (!compareDisabled || compareSelected) onCompareToggle(service); }}
          title={compareSelected ? 'Remove from comparison' : compareDisabled ? 'Max 3 services' : 'Add to compare'}
        >
          {compareSelected ? <CheckIcon style={{ fontSize: '0.8rem' }} /> : <AddCircleOutlineIcon style={{ fontSize: '0.8rem' }} />}
          {compareSelected ? 'Added' : 'Compare'}
        </button>
      </div>
    </article>
  );
};

// ── ServiceListItem ────────────────────────────────────────────────────────────
const ServiceListItem = ({ service, onClick, compareSelected, onCompareToggle, compareDisabled }) => {
  const cfg       = getFieldConfig(service.serviceCategory);
  const status    = getStatus(service.startDate, service.endDate);
  const free      = isFreeService(service);
  const closeSoon = isClosingSoon(service.endDate);

  return (
    <article
      className={`sexp-list-item sexp-list-item--${status}${compareSelected ? ' sexp-list-item--comparing' : ''}`}
      onClick={() => onClick(service)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick(service)}
    >
      <div className={`sexp-list-item__bar sexp-list-item__bar--${status}`} />
      <div className="sexp-list-item__body">
        <div className="sexp-list-item__top">
          <span className={`sexp-status sexp-status--${status} sexp-status--sm`}>{status}</span>
          {closeSoon && <span className="sexp-tag sexp-tag--urgent sexp-tag--sm"><LocalFireDepartmentIcon style={{ fontSize: '0.65rem' }} />Closing Soon</span>}
          {free && <span className="sexp-tag sexp-tag--free sexp-tag--sm">FREE</span>}
        </div>
        <h3 className="sexp-list-item__name">{service.serviceName}</h3>
        <div className="sexp-list-item__meta">
          <span className="sexp-list-item__cat">{service.serviceCategory}</span>
          {service.state && (
            <span className="sexp-list-item__state">
              {service.state === 'All India'
                ? <><PublicIcon style={{ fontSize: '0.7rem' }} /> Central Govt</>
                : <><LocationOnOutlinedIcon style={{ fontSize: '0.7rem' }} />{service.state}</>
              }
            </span>
          )}
          {cfg.qualifications && service.qualifications?.slice(0, 2).filter(Boolean).map((q) => (
            <span key={q} className="sexp-list-item__qual">{q}</span>
          ))}
        </div>
      </div>
      <div className="sexp-list-item__right">
        <span className="sexp-list-item__fee">
          {free ? 'FREE' : `₹${service.feeGeneral}`}
        </span>
        {cfg.dates && service.endDate && (
          <span className="sexp-list-item__date">
            <EventBusyOutlinedIcon style={{ fontSize: '0.7rem' }} />
            {fmtDate(service.endDate)}
          </span>
        )}
      </div>
      <button
        className={`sexp-list-item__compare-btn${compareSelected ? ' sexp-list-item__compare-btn--on' : ''}${compareDisabled ? ' sexp-list-item__compare-btn--disabled' : ''}`}
        onClick={(e) => { e.stopPropagation(); if (!compareDisabled || compareSelected) onCompareToggle(service); }}
        title={compareSelected ? 'Remove from comparison' : compareDisabled ? 'Max 3 services' : 'Add to compare'}
      >
        {compareSelected ? <CheckIcon style={{ fontSize: '0.8rem' }} /> : <AddCircleOutlineIcon style={{ fontSize: '0.8rem' }} />}
      </button>
      <ArrowForwardIcon className="sexp-list-item__arrow" fontSize="small" />
    </article>
  );
};

// ── MobileFilterPanel ─────────────────────────────────────────────────────────
const MobileFilterPanel = ({
  filterOptions, selectedStates, selectedQualifications, selectedCategories,
  selectedStatus, commonOnly, freeOnly, closingSoon, centralGovt,
  setSelectedStates, setSelectedQualifications, setSelectedCategories,
  setSelectedStatus, setCommonOnly, setFreeOnly, setClosingSoon, setCentralGovt,
  onClose, onClearAll,
}) => {
  const toggle = (value, list, setList) =>
    setList((p) => p.includes(value) ? p.filter((v) => v !== value) : [...p, value]);

  const Section = ({ title, children }) => (
    <div className="sexp-mob-section">
      <p className="sexp-mob-section__title">{title}</p>
      {children}
    </div>
  );

  return (
    <div className="sexp-mob-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sexp-mob-panel">
        <div className="sexp-mob-panel__header">
          <span className="sexp-mob-panel__title"><FilterListIcon fontSize="small" /> Filters</span>
          <button className="sexp-mob-panel__close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="sexp-mob-panel__body">
          <Section title="Status">
            <div className="sexp-mob-chips">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  className={`sexp-mob-chip${selectedStatus === s.value ? ' sexp-mob-chip--active' : ''}`}
                  onClick={() => setSelectedStatus(s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </Section>

          <Section title="State">
            <div className="sexp-mob-chips sexp-mob-chips--wrap">
              {filterOptions.states.map((s) => (
                <button
                  key={s}
                  className={`sexp-mob-chip${selectedStates.includes(s) ? ' sexp-mob-chip--active' : ''}`}
                  onClick={() => toggle(s, selectedStates, setSelectedStates)}
                >
                  {s}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Qualification">
            <div className="sexp-mob-chips sexp-mob-chips--wrap">
              {filterOptions.qualifications.map((q) => (
                <button
                  key={q}
                  className={`sexp-mob-chip${selectedQualifications.includes(q) ? ' sexp-mob-chip--active' : ''}`}
                  onClick={() => toggle(q, selectedQualifications, setSelectedQualifications)}
                >
                  {q}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Category">
            <div className="sexp-mob-chips sexp-mob-chips--wrap">
              {filterOptions.categories.map((c) => (
                <button
                  key={c}
                  className={`sexp-mob-chip${selectedCategories.includes(c) ? ' sexp-mob-chip--active' : ''}`}
                  onClick={() => toggle(c, selectedCategories, setSelectedCategories)}
                >
                  {c}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Smart Filters">
            <div className="sexp-mob-chips">
              {[
                { label: 'Common Services', state: commonOnly, set: setCommonOnly, icon: <VerifiedOutlinedIcon style={{ fontSize: '0.85rem' }} /> },
                { label: 'Free / No Fee',   state: freeOnly,   set: setFreeOnly,   icon: <MoneyOffOutlinedIcon style={{ fontSize: '0.85rem' }} /> },
                { label: 'Closing Soon',    state: closingSoon, set: setClosingSoon, icon: <LocalFireDepartmentIcon style={{ fontSize: '0.85rem' }} /> },
                { label: 'Central Govt',   state: centralGovt, set: setCentralGovt, icon: <PublicIcon style={{ fontSize: '0.85rem' }} /> },
              ].map(({ label, state, set, icon }) => (
                <button
                  key={label}
                  className={`sexp-mob-chip sexp-mob-chip--smart${state ? ' sexp-mob-chip--active' : ''}`}
                  onClick={() => set((p) => !p)}
                >
                  {icon}{label}
                </button>
              ))}
            </div>
          </Section>
        </div>

        <div className="sexp-mob-panel__footer">
          <button className="sexp-mob-clear" onClick={onClearAll}>Clear All</button>
          <button className="sexp-mob-apply" onClick={onClose}>Show Results</button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const ServicesExplorer = () => {
  const navigate = useNavigate();

  const [services,              setServices]              = useState([]);
  const [searchQuery,           setSearchQuery]           = useState('');
  const [selectedStates,        setSelectedStates]        = useState([]);
  const [selectedQualifications,setSelectedQualifications]= useState([]);
  const [selectedCategories,    setSelectedCategories]    = useState([]);
  const [selectedStatus,        setSelectedStatus]        = useState('all');
  const [commonOnly,            setCommonOnly]            = useState(false);
  const [freeOnly,              setFreeOnly]              = useState(false);
  const [closingSoon,           setClosingSoon]           = useState(false);
  const [centralGovt,           setCentralGovt]           = useState(false);
  const [sortBy,                setSortBy]                = useState('latest');
  const [viewMode,              setViewMode]              = useState('grid');
  const [mobileFiltersOpen,     setMobileFiltersOpen]     = useState(false);
  const [activeDropdown,        setActiveDropdown]        = useState(null);
  const [compareList,           setCompareList]           = useState([]);
  const [compareModalOpen,      setCompareModalOpen]      = useState(false);
  const searchInputRef = useRef(null);

  const handleCompareToggle = useCallback((svc) => {
    setCompareList((prev) => {
      const key = svc.serviceName;
      if (prev.find((s) => s.serviceName === key)) return prev.filter((s) => s.serviceName !== key);
      if (prev.length >= 3) return prev;
      return [...prev, svc];
    });
  }, []);

  // ── Load + deduplicate ──────────────────────────────────────────────────────
  useEffect(() => {
    seedMockDataIfNeeded();
    setServices(deduplicateServices(getServicesFromStorage()));
  }, []);

  // ── Extract filter options ──────────────────────────────────────────────────
  const filterOptions = useMemo(() => {
    const states = new Set();
    const qualifications = new Set();
    const categories = new Set();
    services.forEach((svc) => {
      if (svc.state && svc.state !== 'All India') states.add(svc.state);
      (svc.qualifications || []).filter(Boolean).forEach((q) => qualifications.add(q));
      if (svc.serviceCategory) categories.add(svc.serviceCategory);
    });
    return {
      states:         [...states].sort(),
      qualifications: [...qualifications].sort(),
      categories:     [...categories].sort(),
    };
  }, [services]);

  // ── Filter + sort ───────────────────────────────────────────────────────────
  const filteredServices = useMemo(() => {
    let result = [...services];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((svc) =>
        svc.serviceName?.toLowerCase().includes(q) ||
        svc.serviceDescription?.toLowerCase().includes(q) ||
        svc.serviceCategory?.toLowerCase().includes(q) ||
        svc.state?.toLowerCase().includes(q) ||
        svc.subCategory?.toLowerCase().includes(q) ||
        (svc.qualifications || []).some((qu) => qu?.toLowerCase().includes(q))
      );
    }
    if (selectedStates.length > 0)
      result = result.filter((svc) => selectedStates.includes(svc.state) || svc.state === 'All India');
    if (selectedQualifications.length > 0)
      result = result.filter((svc) => (svc.qualifications || []).some((q) => selectedQualifications.includes(q)));
    if (selectedCategories.length > 0)
      result = result.filter((svc) => selectedCategories.includes(svc.serviceCategory));
    if (selectedStatus !== 'all')
      result = result.filter((svc) => getStatus(svc.startDate, svc.endDate) === selectedStatus);
    if (commonOnly)
      result = result.filter((svc) => COMMON_CATEGORIES.has(svc.serviceCategory));
    if (freeOnly)
      result = result.filter((svc) => isFreeService(svc));
    if (closingSoon)
      result = result.filter((svc) => isClosingSoon(svc.endDate));
    if (centralGovt)
      result = result.filter((svc) => svc.state === 'All India');

    switch (sortBy) {
      case 'closing-soon':
        result.sort((a, b) =>
          (a.endDate ? new Date(a.endDate) : new Date('9999-12-31')) -
          (b.endDate ? new Date(b.endDate) : new Date('9999-12-31'))
        );
        break;
      case 'name':
        result.sort((a, b) => (a.serviceName || '').localeCompare(b.serviceName || ''));
        break;
      case 'fee-low':
        result.sort((a, b) => (parseInt(a.feeGeneral) || 0) - (parseInt(b.feeGeneral) || 0));
        break;
      default: // latest
        result.sort((a, b) =>
          (b.startDate ? new Date(b.startDate) : new Date(0)) -
          (a.startDate ? new Date(a.startDate) : new Date(0))
        );
    }
    return result;
  }, [services, searchQuery, selectedStates, selectedQualifications, selectedCategories,
      selectedStatus, commonOnly, freeOnly, closingSoon, centralGovt, sortBy]);

  // ── Derived values ──────────────────────────────────────────────────────────
  const activeFilterChips = useMemo(() => [
    ...selectedStates.map((s) => ({ key: `state-${s}`, label: s, onRemove: () => setSelectedStates((p) => p.filter((v) => v !== s)) })),
    ...selectedQualifications.map((q) => ({ key: `qual-${q}`, label: q, onRemove: () => setSelectedQualifications((p) => p.filter((v) => v !== q)) })),
    ...selectedCategories.map((c) => ({ key: `cat-${c}`, label: c, onRemove: () => setSelectedCategories((p) => p.filter((v) => v !== c)) })),
    ...(selectedStatus !== 'all' ? [{ key: 'status', label: `Status: ${selectedStatus}`, onRemove: () => setSelectedStatus('all') }] : []),
    ...(commonOnly  ? [{ key: 'common',  label: 'Common Services', onRemove: () => setCommonOnly(false)  }] : []),
    ...(freeOnly    ? [{ key: 'free',    label: 'Free Only',       onRemove: () => setFreeOnly(false)    }] : []),
    ...(closingSoon ? [{ key: 'closing', label: 'Closing Soon',    onRemove: () => setClosingSoon(false) }] : []),
    ...(centralGovt ? [{ key: 'central', label: 'Central Govt',    onRemove: () => setCentralGovt(false) }] : []),
  ], [selectedStates, selectedQualifications, selectedCategories, selectedStatus, commonOnly, freeOnly, closingSoon, centralGovt]);

  const activeFiltersCount = activeFilterChips.length;

  const clearAllFilters = useCallback(() => {
    setSelectedStates([]); setSelectedQualifications([]); setSelectedCategories([]);
    setSelectedStatus('all'); setCommonOnly(false); setFreeOnly(false);
    setClosingSoon(false); setCentralGovt(false); setSearchQuery('');
  }, []);

  const handleServiceClick = useCallback((svc) => {
    const cat  = encodeURIComponent(svc.serviceCategory || 'General');
    const name = encodeURIComponent(svc.serviceName     || '');
    navigate(`/services/${cat}/${name}`);
  }, [navigate]);

  const toggleDropdown = (key) => setActiveDropdown((p) => (p === key ? null : key));
  const toggle = (value, list, setList) =>
    setList((p) => p.includes(value) ? p.filter((v) => v !== value) : [...p, value]);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="sexp-root">

      {/* ── Page header ── */}
      <div className="sexp-page-header">
        <div className="sexp-page-header__left">
          <h1 className="sexp-page-header__title">All Services</h1>
          <p className="sexp-page-header__count">
            {filteredServices.length} of {services.length} services
            {activeFiltersCount > 0 ? ` · ${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''} applied` : ''}
          </p>
        </div>
        <div className="sexp-page-header__right">
          <SortDropdown value={sortBy} onChange={setSortBy} />
          <div className="sexp-view-toggle">
            <button
              className={`sexp-view-btn${viewMode === 'grid' ? ' sexp-view-btn--active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <GridViewIcon fontSize="small" />
            </button>
            <button
              className={`sexp-view-btn${viewMode === 'list' ? ' sexp-view-btn--active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <ViewListIcon fontSize="small" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Search bar ── */}
      <div className="sexp-search-wrap">
        <SearchIcon className="sexp-search-icon" />
        <input
          ref={searchInputRef}
          className="sexp-search-input"
          placeholder="Search services, categories, states, qualifications…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="sexp-search-clear" onClick={() => setSearchQuery('')} title="Clear search">
            <CloseIcon fontSize="small" />
          </button>
        )}
      </div>

      {/* ── Desktop filter bar ── */}
      <div className="sexp-filter-bar">
        {/* Status tabs */}
        <div className="sexp-status-tabs">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.value}
              className={`sexp-status-tab${selectedStatus === s.value ? ' sexp-status-tab--active' : ''}`}
              onClick={() => setSelectedStatus(s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="sexp-filter-bar__sep" />

        {/* Dropdown filters */}
        <FilterDropdown
          label="State"
          options={filterOptions.states}
          selected={selectedStates}
          onToggle={(v) => toggle(v, selectedStates, setSelectedStates)}
          onClear={() => setSelectedStates([])}
          isOpen={activeDropdown === 'state'}
          onToggleOpen={() => toggleDropdown('state')}
        />
        <FilterDropdown
          label="Qualification"
          options={filterOptions.qualifications}
          selected={selectedQualifications}
          onToggle={(v) => toggle(v, selectedQualifications, setSelectedQualifications)}
          onClear={() => setSelectedQualifications([])}
          isOpen={activeDropdown === 'qualification'}
          onToggleOpen={() => toggleDropdown('qualification')}
        />
        <FilterDropdown
          label="Category"
          options={filterOptions.categories}
          selected={selectedCategories}
          onToggle={(v) => toggle(v, selectedCategories, setSelectedCategories)}
          onClear={() => setSelectedCategories([])}
          isOpen={activeDropdown === 'category'}
          onToggleOpen={() => toggleDropdown('category')}
        />

        <div className="sexp-filter-bar__sep" />

        {/* Smart toggles */}
        <SmartToggle
          label="Common Services"
          icon={<VerifiedOutlinedIcon style={{ fontSize: '0.9rem' }} />}
          active={commonOnly}
          onClick={() => setCommonOnly((p) => !p)}
        />
        <SmartToggle
          label="Free Only"
          icon={<MoneyOffOutlinedIcon style={{ fontSize: '0.9rem' }} />}
          active={freeOnly}
          onClick={() => setFreeOnly((p) => !p)}
        />
        <SmartToggle
          label="Closing Soon"
          icon={<LocalFireDepartmentIcon style={{ fontSize: '0.9rem' }} />}
          active={closingSoon}
          onClick={() => setClosingSoon((p) => !p)}
        />
        <SmartToggle
          label="Central Govt"
          icon={<PublicIcon style={{ fontSize: '0.9rem' }} />}
          active={centralGovt}
          onClick={() => setCentralGovt((p) => !p)}
        />
      </div>

      {/* ── Active filter chips ── */}
      {activeFilterChips.length > 0 && (
        <div className="sexp-chips-bar">
          {activeFilterChips.map((chip) => (
            <button key={chip.key} className="sexp-chip" onClick={chip.onRemove}>
              {chip.label}
              <CloseIcon style={{ fontSize: '0.7rem' }} />
            </button>
          ))}
          <button className="sexp-chip-clear-all" onClick={clearAllFilters}>
            Clear all
          </button>
        </div>
      )}

      {/* ── Results ── */}
      {filteredServices.length === 0 ? (
        <div className="sexp-empty">
          <SentimentDissatisfiedOutlinedIcon className="sexp-empty__icon" />
          <h3 className="sexp-empty__title">No services found</h3>
          <p className="sexp-empty__sub">Try adjusting your search or removing some filters.</p>
          <button className="sexp-empty__btn" onClick={clearAllFilters}>Clear all filters</button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="sexp-grid">
          {filteredServices.map((svc) => {
            const isSelected = !!compareList.find((s) => s.serviceName === svc.serviceName);
            return (
              <ServiceGridCard
                key={svc.id || svc.serviceName}
                service={svc}
                onClick={handleServiceClick}
                compareSelected={isSelected}
                onCompareToggle={handleCompareToggle}
                compareDisabled={!isSelected && compareList.length >= 3}
              />
            );
          })}
        </div>
      ) : (
        <div className="sexp-listview">
          {filteredServices.map((svc) => {
            const isSelected = !!compareList.find((s) => s.serviceName === svc.serviceName);
            return (
              <ServiceListItem
                key={svc.id || svc.serviceName}
                service={svc}
                onClick={handleServiceClick}
                compareSelected={isSelected}
                onCompareToggle={handleCompareToggle}
                compareDisabled={!isSelected && compareList.length >= 3}
              />
            );
          })}
        </div>
      )}

      {/* ── Mobile filter FAB ── */}
      <button
        className="sexp-mob-fab"
        onClick={() => setMobileFiltersOpen(true)}
      >
        <TuneIcon fontSize="small" />
        Filters
        {activeFiltersCount > 0 && <span className="sexp-mob-fab__badge">{activeFiltersCount}</span>}
      </button>

      {/* ── Compare sticky bar ── */}
      {compareList.length >= 1 && (
        <div className={`sexp-compare-bar${compareList.length >= 2 ? ' sexp-compare-bar--ready' : ''}`}>
          <div className="sexp-compare-bar__slots">
            {compareList.map((svc) => (
              <div key={svc.serviceName} className="sexp-compare-bar__slot">
                <span className="sexp-compare-bar__slot-name">{svc.serviceName}</span>
                <button
                  className="sexp-compare-bar__slot-remove"
                  onClick={() => handleCompareToggle(svc)}
                  title="Remove"
                >
                  <CloseIcon style={{ fontSize: '0.7rem' }} />
                </button>
              </div>
            ))}
            {compareList.length < 3 && (
              <div className="sexp-compare-bar__slot sexp-compare-bar__slot--empty">
                <AddCircleOutlineIcon style={{ fontSize: '1rem', opacity: 0.4 }} />
                <span>Add service</span>
              </div>
            )}
          </div>
          <div className="sexp-compare-bar__actions">
            <span className="sexp-compare-bar__hint">
              {compareList.length < 2 ? 'Select at least 2 to compare' : `${compareList.length} selected`}
            </span>
            <button
              className="sexp-compare-bar__clear"
              onClick={() => setCompareList([])}
            >
              Clear
            </button>
            <button
              className="sexp-compare-bar__cta"
              disabled={compareList.length < 2}
              onClick={() => setCompareModalOpen(true)}
            >
              <CompareArrowsIcon style={{ fontSize: '1rem' }} />
              Compare Now
            </button>
          </div>
        </div>
      )}

      {/* ── Compare modal ── */}
      {compareModalOpen && (
        <CompareModal
          services={compareList}
          onClose={() => setCompareModalOpen(false)}
          onNavigate={(svc) => { setCompareModalOpen(false); handleServiceClick(svc); }}
        />
      )}

      {/* ── Mobile filter panel ── */}
      {mobileFiltersOpen && (
        <MobileFilterPanel
          filterOptions={filterOptions}
          selectedStates={selectedStates}
          selectedQualifications={selectedQualifications}
          selectedCategories={selectedCategories}
          selectedStatus={selectedStatus}
          commonOnly={commonOnly}
          freeOnly={freeOnly}
          closingSoon={closingSoon}
          centralGovt={centralGovt}
          setSelectedStates={setSelectedStates}
          setSelectedQualifications={setSelectedQualifications}
          setSelectedCategories={setSelectedCategories}
          setSelectedStatus={setSelectedStatus}
          setCommonOnly={setCommonOnly}
          setFreeOnly={setFreeOnly}
          setClosingSoon={setClosingSoon}
          setCentralGovt={setCentralGovt}
          onClose={() => setMobileFiltersOpen(false)}
          onClearAll={clearAllFilters}
        />
      )}
    </div>
  );
};

export default ServicesExplorer;
